import React, { useMemo } from 'react'

import { GenericBoxResult } from '@dataplace.ai/ui-components/organisms'
import styled, { css } from 'styled-components'
import { TileFormSpan } from '@dataplace.ai/ui-components/atoms'
import { useTranslation } from 'react-i18next'
import { BOX_KEYS, BOXES } from '@dataplace.ai/ui-components/organisms/GenericBoxResult/utils/boxes'
import { useSelector } from 'react-redux'
import { RootState } from 'apps/placeme/src/redux/store'
import { BasicLayers, FeatureCollectionLayers, GeojsonLayers, WMSLayers } from '@dataplace.ai/ui-components/atoms/MapTile/@types/LayersTypes'
import { IAlabAttractivenessTileData } from './@types/IAlabAttractivenessTileData'
import MapVisualisation from '../Surroundings/Investments/components/MapVisualisation'
import { Settings } from '../Mcd/Settings'
import { useSelectedSettings } from './hooks/useSelectedSettings'

const Wrapper = styled.div<{ $background: 'light' | 'dark' }>(({
  theme,
  $background,
}) => {
  const { palette } = theme
  return css`
    display: flex;
    flex-direction: column;
    padding: 1.25rem 1.5rem;
    background-color: ${$background === 'light' ? palette.light.white : palette.light.main};
` })

export const AlabAttractivenessResult: React.FC<{ data: IAlabAttractivenessTileData }> = ({ data }) => {
  const { t } = useTranslation()
  const response = data?.value?.attractiveness
  const resultKey = response?.attractiveness.toLowerCase()
  const percentile = response?.percentile ? Number(response.percentile.toFixed(2)) : response?.percentile
  const settingList = useSelectedSettings(data?.value)

  const {
    catchmentId,
    map: { features },
  } = response || {}

  const { value } = useSelector((state: RootState) => state.location)

  const resultDescription = useMemo(() => {
    switch (resultKey) {
      case BOX_KEYS.under:
        return t('placeme.tile.attractiveness.result.description.under')
      default:
        return t('placeme.tile.attractiveness.result.description.other', {
          percentile,
        })
    }
  }, [resultKey])

  const getLayers = () => {
    if (!features) return
    const mapFeatures = features.map(feature => {
      const box = BOXES.find(box => box.key === feature.properties.segment)

      return ({
        geometry: feature.geometry,
        properties: {
          ...feature.properties,
          style: {
            color: box?.color,
            fillColor: box?.color,
          },
          title: (
            `
            <div>
              <span>
                ${t('placeme.attractiveness.map.feature_tooltip.segment_text')}: ${t(box?.label ?? '')}
              </span>
            </div>`
          ),
        },
        type: 'Feature',
      })
    })

    const layers: (BasicLayers | GeojsonLayers | WMSLayers | FeatureCollectionLayers)[] = []

    layers.push({
      id: `attractiveness_layer_${catchmentId}`,
      layer: {
        data: {
          features: mapFeatures,
          type: 'FeatureCollection',
        },
        options: {
          type: 'geojson',
          id: 'alab_attractiveness_layer_map',
        },
      },
    })

    return layers
  }

  const layers = useMemo(() => getLayers(), [features])

  return (
    <Wrapper $background='light'>
      <Settings selectedSettings={settingList} />
      <GenericBoxResult
        hasData={!!data?.value}
        i18nKeys={{
          header: 'placeme.tile.attractiveness.result.header',
          paragraph: 'placeme.tile.attractiveness.result.paragraph',
          activeLabel: 'placeme.tile.attractiveness.result',
        }}
        loading={data?.loading}
        resultKey={resultKey}
      />
      <Wrapper $background='light'>
        <TileFormSpan isBold>{resultDescription}</TileFormSpan>
      </Wrapper>
      {value
        ? (
          <MapVisualisation
            addMapHeader='placeme.attractiveness.map.add_map.header'
            addMapTitle='placeme.attractiveness.map.add_map'
            catchmentId={catchmentId}
            locationData={value}
            mapLayers={layers}
          />
        )
        : null}
    </Wrapper>
  )
}
